import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import {
  FileUp,
  GripVertical,
  MousePointerClick,
  Pencil,
  Play,
  Plus,
  RefreshCw,
  RotateCcw,
  Trash2,
  Upload,
  X,
} from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { useConfirm } from "@/components/feedback/ConfirmDialog";
import { EmptyState } from "@/components/feedback/EmptyState";
import { toast } from "@/components/feedback/ToastHost";
import { errorMessage } from "@/lib/errors";
import { PdfMappingBuilder } from "@/components/admin/PdfMappingBuilder";
import {
  addMappingAlias,
  createMapping,
  createSource,
  deleteMapping,
  deleteMappingAlias,
  deleteSource,
  discardRow,
  getRun,
  getRunRows,
  listMappings,
  listRuns,
  listSources,
  pollSource,
  previewManualImport,
  reorderMappings,
  replayRun,
  runManualImport,
  updateMapping,
  updateSource,
  type ImportSourceInput,
} from "@/services/api/imports";
import { listMailboxes } from "@/services/api/mailboxes";
import {
  createPdfExtractor,
  deletePdfExtractor,
  listPdfExtractors,
  updatePdfExtractor,
  uploadInvoicePdf,
} from "@/services/api/invoiceDocuments";
import type {
  ColumnMapping,
  ImportRow,
  ImportRun,
  ImportSource,
  ImportType,
  ManualImportPreview,
  PdfExtractor,
} from "@/services/api/types";
import { IMPORT_TYPES } from "@/services/api/types";

const IMPORT_TYPE_LABELS: Record<ImportType, string> = {
  shipping_request: "Shipping request",
  depot_crystal: "Depot / Crystal stock",
  customer: "Customer master",
  cse: "Customer Service Executive (CSE)",
  haulier: "Haulier master",
  contact: "Contact",
  invoice: "Invoice (PDF)",
};

// Grid-based document types (CSV/XLS/XLSX). PDF types (invoice) use the
// separate PDF-extractor tab and are not offered by the column-mapping/manual
// grid flows.
const GRID_IMPORT_TYPES = IMPORT_TYPES.filter((item) => item !== "invoice");

function statusVariant(status: string): "success" | "warning" | "destructive" | "secondary" {
  if (status === "completed") return "success";
  if (status === "failed") return "destructive";
  if (status === "running") return "warning";
  return "secondary";
}

function originLabel(origin: string): string {
  if (origin === "manual") return "Manual";
  if (origin === "replay") return "Replay";
  return "Email";
}

/** How polling works — shown at the top of the Sources tab for transparency. */
function PollingExplainer() {
  return (
    <div className="rounded-md border border-muted bg-muted/30 p-4 text-sm text-muted-foreground">
      <p className="font-medium text-foreground">How polling works</p>
      <ul className="mt-2 list-inside list-disc space-y-1">
        <li>
          Each enabled source is polled on its own interval (the “Poll every” value, in minutes).
        </li>
        <li>
          On each poll the scheduler connects to the source&apos;s mailbox and reads messages that
          are <em>not</em> older than the poll: any message whose sender is in the allowlist and
          whose subject contains the subject filter.
        </li>
        <li>
          <strong>Every attachment</strong> on a matching message is imported — attachment
          filenames are <em>not</em> restricted.
        </li>
        <li>
          The &ldquo;After import&rdquo; setting on the mailbox decides what happens to the source
          message once its attachments have been imported successfully.
        </li>
      </ul>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Sources tab
// ---------------------------------------------------------------------------

function SourceEditor({
  source,
  mailboxes,
  onClose,
}: {
  source: ImportSource | null;
  mailboxes: { id: number; username: string }[];
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [draft, setDraft] = useState<ImportSourceInput>(
    source
      ? {
          code: source.code,
          mailbox_account_id: source.mailbox_account_id,
          import_type: source.import_type,
          subject_filter: source.subject_filter,
          sender_allowlist: source.sender_allowlist,
          poll_interval_minutes: source.poll_interval_minutes,
          is_enabled: source.is_enabled,
        }
      : {
          code: "",
          mailbox_account_id: mailboxes[0]?.id ?? null,
          import_type: "shipping_request",
          subject_filter: "",
          sender_allowlist: [],
          poll_interval_minutes: 15,
          is_enabled: false,
        },
  );
  const [allowlistText, setAllowlistText] = useState(
    (draft.sender_allowlist ?? []).join(", "),
  );

  const save = useMutation({
    mutationFn: () => {
      const body: ImportSourceInput = {
        ...draft,
        sender_allowlist: allowlistText
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean),
      };
      return source ? updateSource(source.id, body) : createSource(body);
    },
    onSuccess: () => {
      toast.success(source ? "Import source saved" : "Import source created");
      void queryClient.invalidateQueries({ queryKey: ["import-sources"] });
      onClose();
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const set = <K extends keyof ImportSourceInput>(key: K, value: ImportSourceInput[K]) =>
    setDraft((prev) => ({ ...prev, [key]: value }));

  return (
    <div className="rounded-md border p-4">
      <Label className="text-sm font-semibold">{source ? `Edit ${source.code}` : "New source"}</Label>
      <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <Label className="text-xs">Code</Label>
          <Input value={draft.code} onChange={(e) => set("code", e.target.value)} />
        </div>
        <div>
          <Label className="text-xs">Mailbox</Label>
          <Select
            value={draft.mailbox_account_id !== null ? String(draft.mailbox_account_id) : "none"}
            onValueChange={(v) => set("mailbox_account_id", v === "none" ? null : Number(v))}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No mailbox</SelectItem>
              {mailboxes.map((mailbox) => (
                <SelectItem key={mailbox.id} value={String(mailbox.id)}>
                  {mailbox.username}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Import type</Label>
          <Select
            value={draft.import_type}
            onValueChange={(v) => set("import_type", v as ImportType)}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {IMPORT_TYPES.map((type) => (
                <SelectItem key={type} value={type}>
                  {IMPORT_TYPE_LABELS[type]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="text-xs">Subject filter (must contain)</Label>
          <Input
            value={draft.subject_filter ?? ""}
            placeholder="e.g. Despatch Summary"
            onChange={(e) => set("subject_filter", e.target.value || null)}
          />
        </div>
        <div>
          <Label className="text-xs">Allowed senders (comma separated)</Label>
          <Input
            value={allowlistText}
            placeholder="carrier@acme.com, depot@acme.com"
            onChange={(e) => setAllowlistText(e.target.value)}
          />
        </div>
        <div>
          <Label className="text-xs">Poll every (minutes)</Label>
          <Input
            type="number"
            value={draft.poll_interval_minutes}
            onChange={(e) => set("poll_interval_minutes", Number(e.target.value) || 1)}
          />
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between">
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          Enabled
          <Switch checked={draft.is_enabled} onCheckedChange={(v) => set("is_enabled", v)} />
        </label>
        <div className="flex items-center gap-2">
          <Button type="button" variant="ghost" size="sm" onClick={onClose}>
            Cancel
          </Button>
          <Button type="button" size="sm" onClick={() => save.mutate()} disabled={save.isPending || !draft.code}>
            Save
          </Button>
        </div>
      </div>
    </div>
  );
}

function SourcesTab() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const { t } = useTranslation();
  const [editing, setEditing] = useState<ImportSource | null>(null);
  const [creating, setCreating] = useState(false);

  const sources = useQuery({ queryKey: ["import-sources"], queryFn: () => listSources() });
  const mailboxes = useQuery({ queryKey: ["mailboxes"], queryFn: () => listMailboxes() });
  const mailboxBy = useMemo(
    () => new Map((mailboxes.data ?? []).map((m) => [m.id, m.username])),
    [mailboxes.data],
  );

  const poll = useMutation({
    mutationFn: (id: number) => pollSource(id),
    onSuccess: (result, id) => {
      toast.success(`Poll complete — ${result.runs} new import${result.runs === 1 ? "" : "s"}`);
      void queryClient.invalidateQueries({ queryKey: ["import-runs"] });
      void queryClient.invalidateQueries({ queryKey: ["import-sources"] });
      void id;
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteSource(id),
    onSuccess: () => {
      toast.success("Import source deleted");
      void queryClient.invalidateQueries({ queryKey: ["import-sources"] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const confirmDelete = async (source: ImportSource) => {
    const ok = await confirm({
      title: t("admin.deleteRecord", { name: source.code }),
      description: "Delete this import source? Its polling job stops immediately.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (ok) remove.mutate(source.id);
  };

  const rows = sources.data?.items ?? [];

  return (
    <div className="space-y-4">
      <PollingExplainer />
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {rows.length} source{rows.length === 1 ? "" : "s"}
        </p>
        <Button type="button" size="sm" onClick={() => setCreating(true)}>
          <Upload className="h-4 w-4" />
          New source
        </Button>
      </div>
      {creating || editing ? (
        <SourceEditor
          source={editing}
          mailboxes={mailboxes.data ?? []}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
        />
      ) : null}
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Mailbox</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Subject filter</TableHead>
              <TableHead>Poll</TableHead>
              <TableHead>Enabled</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((source) => (
              <TableRow key={source.id}>
                <TableCell className="font-medium">{source.code}</TableCell>
                <TableCell className="font-mono text-xs">
                  {source.mailbox_account_id === null
                    ? "—"
                    : (mailboxBy.get(source.mailbox_account_id) ?? `#${source.mailbox_account_id}`)}
                </TableCell>
                <TableCell>{IMPORT_TYPE_LABELS[source.import_type]}</TableCell>
                <TableCell>{source.subject_filter ?? "—"}</TableCell>
                <TableCell>{source.poll_interval_minutes} min</TableCell>
                <TableCell>
                  <Switch checked={source.is_enabled} disabled />
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      title="Poll now"
                      onClick={() => poll.mutate(source.id)}
                    >
                      <Play className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      title="Edit"
                      onClick={() => {
                        setEditing(source);
                        setCreating(false);
                      }}
                    >
                      Edit
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      title="Delete"
                      onClick={() => void confirmDelete(source)}
                    >
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <EmptyState title="No import sources" description="Create one to start polling a mailbox." />
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Runs tab
// ---------------------------------------------------------------------------

function RunDetail({ run }: { run: ImportRun }) {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const runId = run.id;
  const rowsQuery = useQuery({
    queryKey: ["import-rows", runId],
    queryFn: () => getRunRows(runId),
    enabled: true,
  });
  const runQuery = useQuery({
    queryKey: ["import-run", run.id],
    queryFn: () => getRun(run.id),
    initialData: run,
  });

  const active = runQuery.data ?? run;

  const replay = useMutation({
    mutationFn: () => replayRun(active.id),
    onSuccess: (result) => {
      toast.success(`Replay created run #${result.id} (${result.rows_applied} applied, ${result.rows_quarantined} quarantined)`);
      void queryClient.invalidateQueries({ queryKey: ["import-runs"] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const discard = useMutation({
    mutationFn: (rowId: number) => discardRow(active.id, rowId),
    onSuccess: () => {
      toast.success("Row discarded");
      void queryClient.invalidateQueries({ queryKey: ["import-rows", active.id] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const confirmReplay = async () => {
    const ok = await confirm({
      title: "Replay this import?",
      description:
        "Re-runs the exact archived file through the current mapping. Fix any column mappings first.",
      confirmLabel: "Replay",
    });
    if (ok) replay.mutate();
  };

  const confirmDiscard = async (row: ImportRow) => {
    const ok = await confirm({
      title: `Discard row ${row.row_number}?`,
      description: "Marks this quarantined row as discarded so it stops showing as open. The raw payload is retained for audit.",
      confirmLabel: "Discard",
      destructive: true,
    });
    if (ok) discard.mutate(row.id);
  };

  const rows = rowsQuery.data ?? [];

  return (
    <div className="rounded-md border p-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant={statusVariant(active.status) as "success" | "warning" | "destructive" | "secondary"}>
            {active.status}
          </Badge>
          <Badge variant="secondary">{originLabel(active.origin)}</Badge>
          <span className="text-sm text-muted-foreground">
            {active.rows_applied} applied · {active.rows_quarantined} quarantined · {active.rows_total} total
          </span>
          <span className="font-mono text-xs text-muted-foreground">{active.message_id}</span>
        </div>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => void confirmReplay()}
          disabled={replay.isPending || !active.archive_path}
          title={active.archive_path ? undefined : "No archived artefact to replay"}
        >
          <RotateCcw className="h-4 w-4" />
          Replay
        </Button>
      </div>
      <div className="mt-3 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Row</TableHead>
              <TableHead>Outcome</TableHead>
              <TableHead>Load</TableHead>
              <TableHead>Error code</TableHead>
              <TableHead>Detail / payload</TableHead>
              <TableHead>Resolution</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.id}>
                <TableCell>{row.row_number}</TableCell>
                <TableCell>
                  <Badge variant={row.outcome === "applied" ? "success" : "destructive"}>
                    {row.outcome}
                  </Badge>
                </TableCell>
                <TableCell>{row.load_id ?? "—"}</TableCell>
                <TableCell className="max-w-md font-mono text-xs">
                  {row.error_code ? (
                    <span>
                      {row.error_code}
                      {row.error_detail ? (
                        <>
                          {" "}
                          <span className="font-sans text-muted-foreground">-</span>{" "}
                          <span className="font-sans">{row.error_detail}</span>
                        </>
                      ) : null}
                    </span>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell className="max-w-md">
                  {row.outcome === "quarantined" ? (
                    <details className="text-xs">
                      <summary className="cursor-pointer">Raw payload</summary>
                      <pre className="mt-1 whitespace-pre-wrap rounded bg-muted p-2">
                        {JSON.stringify(row.raw_payload, null, 2)}
                      </pre>
                    </details>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell>
                  {row.resolution ? (
                    <Badge variant={row.resolution === "discarded" ? "destructive" : "success"}>
                      {row.resolution}
                    </Badge>
                  ) : (
                    "—"
                  )}
                </TableCell>
                <TableCell className="text-right">
                  {row.outcome === "quarantined" && !row.resolution ? (
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => void confirmDiscard(row)}
                    >
                      Discard
                    </Button>
                  ) : null}
                </TableCell>
              </TableRow>
            ))}
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <EmptyState title="No staged rows" description="This run produced no staged rows." />
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function RunsTab() {
  const queryClient = useQueryClient();
  const sources = useQuery({ queryKey: ["import-sources"], queryFn: () => listSources() });
  const sourceBy = useMemo(
    () => new Map((sources.data?.items ?? []).map((s) => [s.id, s.code])),
    [sources.data],
  );
  const [selected, setSelected] = useState<ImportRun | null>(null);
  const runs = useQuery({
    queryKey: ["import-runs"],
    queryFn: () => listRuns({ limit: 100 }),
    refetchInterval: 15_000,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ["import-runs"] });
    if (selected) {
      void queryClient.invalidateQueries({ queryKey: ["import-run", selected.id] });
      void queryClient.invalidateQueries({ queryKey: ["import-rows", selected.id] });
    }
  };

  const rows = runs.data?.items ?? [];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          Latest runs — every email, manual and replay import is listed here with its outcome.
        </p>
        <Button type="button" variant="outline" size="sm" onClick={refresh}>
          <RefreshCw className="h-4 w-4" />
          Refresh
        </Button>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Run</TableHead>
              <TableHead>Origin</TableHead>
              <TableHead>Source</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Applied</TableHead>
              <TableHead>Quarantined</TableHead>
              <TableHead>Started</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((run) => (
              <TableRow
                key={run.id}
                className={selected?.id === run.id ? "bg-muted/40" : undefined}
                onClick={() => setSelected(run)}
              >
                <TableCell className="font-mono text-xs">#{run.id}</TableCell>
                <TableCell>
                  <Badge variant="secondary">{originLabel(run.origin)}</Badge>
                </TableCell>
                <TableCell>
                  {run.import_source_id === null
                    ? "—"
                    : (sourceBy.get(run.import_source_id) ?? `#${run.import_source_id}`)}
                </TableCell>
                <TableCell>
                  <Badge variant={statusVariant(run.status) as "success" | "warning" | "destructive" | "secondary"}>
                    {run.status}
                  </Badge>
                </TableCell>
                <TableCell>{run.rows_applied}</TableCell>
                <TableCell>{run.rows_quarantined}</TableCell>
                <TableCell>{new Date(run.started_at).toLocaleString()}</TableCell>
              </TableRow>
            ))}
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7}>
                  <EmptyState title="No import runs yet" description="Imports will appear here as email, manual or replay runs complete." />
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>
      {selected ? <RunDetail run={selected} /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mappings tab
// ---------------------------------------------------------------------------

/** Reusable per-type field → aliases editor (README: flexible column mapping). */
function MappingsTab() {
  const queryClient = useQueryClient();
  const [type, setType] = useState<ImportType>("shipping_request");
  const [newField, setNewField] = useState("");
  const [aliasDraft, setAliasDraft] = useState<Record<number, string>>({});
  const [items, setItems] = useState<ColumnMapping[]>([]);
  const dragIndex = useRef<number | null>(null);
  const overIndex = useRef<number | null>(null);

  const mappings = useQuery({
    queryKey: ["mappings", type],
    queryFn: () => listMappings(type),
  });

  // Keep the draggable list in sync with the server's saved order.
  useEffect(() => {
    setItems(mappings.data ?? []);
  }, [mappings.data]);

  const toggleRequired = useMutation({
    mutationFn: (mapping: ColumnMapping) =>
      updateMapping(mapping.id, { is_required: !mapping.is_required }),
    onSuccess: () => {
      toast.success("Field updated");
      void queryClient.invalidateQueries({ queryKey: ["mappings", type] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: (id: number) => deleteMapping(id),
    onSuccess: () => {
      toast.success("Field deleted");
      void queryClient.invalidateQueries({ queryKey: ["mappings", type] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const addAlias = useMutation({
    mutationFn: ({ mappingId, header }: { mappingId: number; header: string }) =>
      addMappingAlias(mappingId, header),
    onSuccess: () => {
      toast.success("Alias added");
      void queryClient.invalidateQueries({ queryKey: ["mappings", type] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const removeAlias = useMutation({
    mutationFn: ({ mappingId, aliasId }: { mappingId: number; aliasId: number }) =>
      deleteMappingAlias(mappingId, aliasId),
    onSuccess: () => {
      toast.success("Alias removed");
      void queryClient.invalidateQueries({ queryKey: ["mappings", type] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const reorder = useMutation({
    mutationFn: (ids: number[]) => reorderMappings(ids),
    onSuccess: () => {
      toast.success("Field order saved");
      void queryClient.invalidateQueries({ queryKey: ["mappings", type] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const addField = useMutation({
    mutationFn: (field: string) =>
      createMapping({
        import_type: type,
        import_source_id: null,
        destination_field: field,
        is_required: false,
        aliases: [],
      }),
    onSuccess: () => {
      toast.success("Field added");
      setNewField("");
      void queryClient.invalidateQueries({ queryKey: ["mappings", type] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const onDragStart = (index: number) => {
    dragIndex.current = index;
    overIndex.current = index;
  };

  const onDragOver = (index: number) => {
    if (dragIndex.current === null || dragIndex.current === index) return;
    overIndex.current = index;
  };

  const onDrop = (targetIndex: number) => {
    const from = dragIndex.current;
    dragIndex.current = null;
    overIndex.current = null;
    if (from === null || from === targetIndex) return;
    const next = [...items];
    const [moved] = next.splice(from, 1);
    next.splice(targetIndex, 0, moved);
    setItems(next);
    reorder.mutate(next.map((mapping) => mapping.id));
  };

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-muted bg-muted/30 p-4 text-sm text-muted-foreground">
        <p className="font-medium text-foreground">Column mappings</p>
        <p className="mt-1">
          Every document type has a reusable list of fields. Each field lists the alternative
          column names (aliases) that may appear in an uploaded file — a column maps to a field when
          its header matches any alias, so a document&apos;s header row can use any of these names.
          Fields marked <span className="text-destructive">*</span> are required: a document missing
          one is rejected and administrators are notified.
        </p>
        <p className="mt-1">
          Drag a field by its <GripVertical className="inline h-3.5 w-3.5" /> handle to reorder the
          fields — the order is saved and reused every import.
        </p>
      </div>
      <div className="flex items-center gap-3">
        <Label className="text-xs">Document type</Label>
        <Select value={type} onValueChange={(v) => setType(v as ImportType)}>
          <SelectTrigger className="w-72">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {GRID_IMPORT_TYPES.map((item) => (
              <SelectItem key={item} value={item}>
                {IMPORT_TYPE_LABELS[item]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-10" />
              <TableHead>Field</TableHead>
              <TableHead className="w-24">Required</TableHead>
              <TableHead>Accepted column names (aliases)</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {items.map((mapping, index) => (
              <TableRow
                key={mapping.id}
                draggable
                onDragStart={() => onDragStart(index)}
                onDragOver={(e) => {
                  e.preventDefault();
                  onDragOver(index);
                }}
                onDragEnter={(e) => {
                  e.preventDefault();
                  onDragOver(index);
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  onDrop(index);
                }}
                onDragEnd={() => {
                  dragIndex.current = null;
                  overIndex.current = null;
                }}
                className={
                  overIndex.current === index && dragIndex.current !== index
                    ? "cursor-grab bg-muted/40"
                    : "cursor-grab"
                }
              >
                <TableCell className="w-10">
                  <GripVertical className="h-4 w-4 text-muted-foreground" />
                </TableCell>
                <TableCell className="font-mono text-sm">
                  {mapping.destination_field}
                  {mapping.is_required ? (
                    <span className="ml-0.5 text-destructive" title="Required field">
                      *
                    </span>
                  ) : null}
                </TableCell>
                <TableCell>
                  <Switch
                    checked={mapping.is_required}
                    onCheckedChange={() => toggleRequired.mutate(mapping)}
                  />
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap items-center gap-1">
                    {mapping.aliases.map((alias) => (
                      <span
                        key={alias.id}
                        className="inline-flex items-center gap-1 rounded bg-muted px-1.5 py-0.5 text-xs"
                      >
                        {alias.source_header}
                        <button
                          type="button"
                          title={`Remove ${alias.source_header}`}
                          className="text-muted-foreground transition-colors hover:text-destructive"
                          onClick={() =>
                            removeAlias.mutate({ mappingId: mapping.id, aliasId: alias.id })
                          }
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </span>
                    ))}
                    <div className="flex items-center gap-1">
                      <Input
                        className="h-6 w-44 text-xs"
                        placeholder="+ alias"
                        value={aliasDraft[mapping.id] ?? ""}
                        onChange={(e) =>
                          setAliasDraft((prev) => ({ ...prev, [mapping.id]: e.target.value }))
                        }
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        disabled={!(aliasDraft[mapping.id] ?? "").trim() || addAlias.isPending}
                        onClick={() => {
                          addAlias.mutate({
                            mappingId: mapping.id,
                            header: (aliasDraft[mapping.id] ?? "").trim(),
                          });
                          setAliasDraft((prev) => ({ ...prev, [mapping.id]: "" }));
                        }}
                      >
                        Add
                      </Button>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    title="Delete field"
                    onClick={() => remove.mutate(mapping.id)}
                  >
                    <Trash2 className="h-4 w-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5}>
                  <EmptyState
                    title="No mappings for this type"
                    description="Add the first field below."
                  />
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>
      <div className="flex items-center gap-2">
        <Input
          className="max-w-xs"
          placeholder="New field name (e.g. vat_number)"
          value={newField}
          onChange={(e) => setNewField(e.target.value)}
        />
        <Button
          type="button"
          size="sm"
          disabled={!newField.trim() || addField.isPending}
          onClick={() => addField.mutate(newField.trim())}
        >
          Add field
        </Button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Manual import tab (2-step: analyse → review mapping → import)
// ---------------------------------------------------------------------------

function ManualImportTab() {
  const queryClient = useQueryClient();
  const [type, setType] = useState<ImportType>("customer");
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<ManualImportPreview | null>(null);
  const [headerRow, setHeaderRow] = useState<number | null>(null);
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [result, setResult] = useState<ImportRun | null>(null);
  const [pendingHeaderRow, setPendingHeaderRow] = useState("");

  const analyze = useMutation({
    mutationFn: () => previewManualImport(type, file as File, headerRow),
    onSuccess: (data) => {
      setPreview(data);
      setResult(null);
      const next: Record<string, string> = {};
      for (const [field, column] of Object.entries(data.field_mapping)) {
        if (column) next[field] = column;
      }
      setMapping(next);
      setHeaderRow(data.header_row);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const doImport = useMutation({
    mutationFn: () => runManualImport(type, file as File, mapping, headerRow),
    onSuccess: (run) => {
      toast.success(
        `Import complete — ${run.rows_applied} applied, ${run.rows_quarantined} quarantined`,
      );
      setResult(run);
      void queryClient.invalidateQueries({ queryKey: ["import-runs"] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  // Invoice PDFs run straight through the PDF extractors — there is no column
  // mapping to review, so a single upload runs extract, match and price-check.
  const doPdfImport = useMutation({
    mutationFn: () => uploadInvoicePdf(file as File),
    onSuccess: (run) => {
      toast.success(
        `Invoice import complete — ${run.rows_applied} applied, ${run.rows_quarantined} quarantined`,
      );
      setResult(run);
      void queryClient.invalidateQueries({ queryKey: ["import-runs"] });
      void queryClient.invalidateQueries({ queryKey: ["invoice-documents"] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const reset = () => {
    setFile(null);
    setPreview(null);
    setMapping({});
    setHeaderRow(null);
    setResult(null);
    setPendingHeaderRow("");
  };

  const columns = useMemo(() => preview?.columns ?? [], [preview]);
  // Each selectbox offers each distinct column header ONCE — a file may contain
  // duplicate headers (e.g. three "Postcode" columns) and duplicate SelectItems
  // break single-select/deselect. The backend merges duplicate headers anyway
  // (name-keyed), so only unique names are selectable.
  const columnOptions = useMemo(() => Array.from(new Set(columns)), [columns]);
  const duplicateColumns = useMemo(() => {
    const seen = new Map<string, number>();
    for (const column of columns) seen.set(column, (seen.get(column) ?? 0) + 1);
    return [...seen.entries()]
      .filter(([, count]) => count > 1)
      .map(([name, count]) => `${name} (×${count})`);
  }, [columns]);
  // Recompute required-field completeness from the CURRENT selectbox mapping,
  // not the analysis-time snapshot, so assigning a required column clears the
  // banner and enables the import immediately.
  const missingRequired = useMemo(() => {
    if (!preview) return [];
    return preview.required_fields.filter(
      (field) => !mapping[field] || mapping[field] === "__none__",
    );
  }, [preview, mapping]);

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-muted bg-muted/30 p-4 text-sm text-muted-foreground">
        Upload the exact same attachment here to run it through the identical pipeline an email
        delivery would use — parsing, column mapping, coercion, reconciliation and staging. Pick a{" "}
        <em>document type</em>. Spreadsheet types (customer, CSE, haulier or one of the feeds) are
        analysed first: the platform identifies the header row and pre-maps every column, and you can
        reassign any field. <em>Invoice</em> PDFs import immediately via the PDF extractors — no column
        mapping to review. Every change is audit-tracked as if you made it manually.
      </div>

      {/* Step 1 — pick the document type and file */}
      {!preview ? (
        <>
          <div className="grid max-w-2xl grid-cols-1 gap-3 sm:grid-cols-3">
            <div>
              <Label className="text-xs">Document type</Label>
              <Select
                value={type}
                onValueChange={(v) => {
                  setType(v as ImportType);
                  setPreview(null);
                  setMapping({});
                  setHeaderRow(null);
                  setResult(null);
                  setPendingHeaderRow("");
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {IMPORT_TYPES.map((item) => (
                    <SelectItem key={item} value={item}>
                      {IMPORT_TYPE_LABELS[item]}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs">Attachment file</Label>
              <Input
                type="file"
                accept={type === "invoice" ? ".pdf,application/pdf" : ".csv,.xls,.xlsx"}
                onChange={(e) => setFile(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>
          {type === "invoice" ? (
            <Button
              type="button"
              onClick={() => doPdfImport.mutate()}
              disabled={!file || doPdfImport.isPending}
            >
              <FileUp className="h-4 w-4" />
              {doPdfImport.isPending ? "Importing…" : "Import invoice PDF"}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={() => analyze.mutate()}
              disabled={!file || analyze.isPending}
            >
              <FileUp className="h-4 w-4" />
              {analyze.isPending ? "Analysing…" : "Analyse file"}
            </Button>
          )}
        </>
      ) : null}

      {/* Step 2 — review the mapping, then import */}
      {preview ? (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex flex-wrap items-center gap-2 text-sm">
              <Badge variant="secondary">{IMPORT_TYPE_LABELS[preview.import_type]}</Badge>
              {preview.header_row_detected ? (
                <Badge variant="success">
                  Header row: {preview.header_row !== null ? preview.header_row + 1 : "?"}
                </Badge>
              ) : (
                <Badge variant="destructive">Header row not identified</Badge>
              )}
              <span className="text-muted-foreground">{columns.length} columns</span>
            </div>
            <Button type="button" variant="ghost" size="sm" onClick={reset}>
              <X className="h-4 w-4" />
              Start over
            </Button>
          </div>

          {/* Header row could not be found → ask for the row number (req 2.2) */}
          {!preview.header_row_detected ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4">
              <Label className="text-sm font-semibold text-destructive">
                Which row holds the column headers?
              </Label>
              <p className="mt-1 text-xs text-muted-foreground">
                The header row couldn&apos;t be identified automatically. Enter the row number (1‑based)
                where the column names are, then re-analyse.
              </p>
              <div className="mt-2 flex items-center gap-2">
                <Input
                  type="number"
                  min={1}
                  className="w-32"
                  value={pendingHeaderRow}
                  placeholder="Row number"
                  onChange={(e) => setPendingHeaderRow(e.target.value)}
                />
                <Button
                  type="button"
                  size="sm"
                  disabled={!pendingHeaderRow || analyze.isPending}
                  onClick={() => {
                    setHeaderRow(Number(pendingHeaderRow) - 1);
                    analyze.mutate();
                  }}
                >
                  Re-analyse from this row
                </Button>
              </div>
            </div>
          ) : null}

          {/* Duplicate column headers (req: single-select must still work) */}
          {duplicateColumns.length > 0 ? (
            <div className="rounded-md border border-warning/40 bg-warning/5 p-4 text-sm">
              <p className="font-semibold text-warning">
                Duplicate column headers: {duplicateColumns.join(", ")}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                Duplicate columns are merged during import — the last column&apos;s value is used for
                that header. To map each one separately, rename the columns in the file so every
                header is unique.
              </p>
            </div>
          ) : null}

          {/* Required fields are missing (req 1.1) */}
          {missingRequired.length > 0 ? (
            <div className="rounded-md border border-destructive/40 bg-destructive/5 p-4 text-sm">
              <p className="font-semibold text-destructive">
                Required field{missingRequired.length > 1 ? "s" : ""} not mapped:
                {missingRequired.map((field) => (
                  <span key={field} className="ml-1 font-mono">
                    *{field}
                  </span>
                ))}
              </p>
              <p className="mt-1 text-xs text-muted-foreground">
                A document missing a required field is rejected and administrators are notified.
                Assign a column below, or add the required column to the file.
              </p>
            </div>
          ) : null}

          <div className="rounded-md border">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-72">Field</TableHead>
                  <TableHead>Column in file</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.keys(preview.field_mapping).map((field) => {
                  const required = preview.required_fields.includes(field);
                  const missing = missingRequired.includes(field);
                  return (
                    <TableRow key={field}>
                      <TableCell className="font-mono text-sm">
                        {field}
                        {required ? (
                          <span className="ml-0.5 text-destructive" title="Required field">
                            *
                          </span>
                        ) : null}
                        {missing ? (
                          <span className="ml-2 text-xs font-sans text-destructive">
                            missing
                          </span>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <Select
                          value={mapping[field] ?? ""}
                          onValueChange={(column) =>
                            setMapping((prev) => {
                              const next = { ...prev };
                              if (column === "__none__") {
                                delete next[field];
                              } else {
                                next[field] = column;
                              }
                              return next;
                            })
                          }
                        >
                          <SelectTrigger className="max-w-md">
                            <SelectValue placeholder="— not mapped —" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="__none__">— not mapped —</SelectItem>
                            {columnOptions.map((column) => (
                              <SelectItem key={column} value={column}>
                                {column}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center gap-3">
            <Button
              type="button"
              onClick={() => doImport.mutate()}
              disabled={doImport.isPending || missingRequired.length > 0}
              title={
                missingRequired.length > 0
                  ? "Map every required field before importing"
                  : undefined
              }
            >
              <Upload className="h-4 w-4" />
              {doImport.isPending ? "Importing…" : "Run import"}
            </Button>
            {missingRequired.length > 0 ? (
              <span className="text-xs text-destructive">
                Import is disabled until every required field is mapped.
              </span>
            ) : null}
          </div>
        </div>
      ) : null}

      {result ? (
        <div className="max-w-2xl rounded-md border p-4">
          <div className="flex flex-wrap items-center gap-2">
            <Badge
              variant={
                statusVariant(result.status) as "success" | "warning" | "destructive" | "secondary"
              }
            >
              {result.status}
            </Badge>
            <span className="text-sm">
              Run #{result.id} — {result.rows_applied} applied · {result.rows_quarantined}{" "}
              quarantined · {result.rows_total} total
            </span>
          </div>
          {result.error ? (
            <p className="mt-2 text-sm text-destructive">{result.error}</p>
          ) : null}
          <p className="mt-2 text-xs text-muted-foreground">
            Open the <em>Runs</em> tab and click this run to inspect every staged row and resolve any
            quarantines.
          </p>
        </div>
      ) : null}
    </div>
  );
}

/** Create/edit form for a single PDF field extractor. */
function ExtractorDialog({
  initial,
  onClose,
  onSave,
}: {
  initial: PdfExtractor | null;
  onClose: () => void;
  onSave: (input: {
    destination_field: string;
    label: string | null;
    pattern: string | null;
    is_required: boolean;
  }) => void;
}) {
  const [field, setField] = useState(initial?.destination_field ?? "");
  const [label, setLabel] = useState(initial?.label ?? "");
  const [pattern, setPattern] = useState(initial?.pattern ?? "");
  const [required, setRequired] = useState(initial?.is_required ?? false);

  const canSave = field.trim().length > 0 && (label.trim().length > 0 || pattern.trim().length > 0);

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{initial ? "Edit extractor" : "Add PDF field"}</DialogTitle>
          <DialogDescription>
            A label (“Invoice Number: INV-123”) and/or a regex pattern whose first capture group is
            the value.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs">Destination field</Label>
            <Input
              value={field}
              disabled={initial !== null}
              placeholder="invoice_number"
              onChange={(e) => setField(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Label</Label>
            <Input
              value={label}
              placeholder="Invoice Number"
              onChange={(e) => setLabel(e.target.value)}
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Regex pattern (first capture group)</Label>
            <Input
              value={pattern}
              placeholder="(?:Invoice|Inv)\s*No[:.]?\s*([A-Za-z0-9\-]+)"
              onChange={(e) => setPattern(e.target.value)}
            />
          </div>
          <div className="flex items-center gap-2">
            <Switch
              checked={required}
              onCheckedChange={(value) => setRequired(Boolean(value))}
              id="extractor-required"
            />
            <Label htmlFor="extractor-required" className="text-xs">
              Required (extraction failure quarantines the document)
            </Label>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={() =>
              onSave({
                destination_field: field.trim(),
                label: label.trim() || null,
                pattern: pattern.trim() || null,
                is_required: required,
              })
            }
            disabled={!canSave}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** PDF field-extractor management (the PDF analogue of column mappings). */
function PdfExtractorsTab() {
  const queryClient = useQueryClient();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<PdfExtractor | null>(null);
  const [creating, setCreating] = useState(false);
  const [builderOpen, setBuilderOpen] = useState(false);

  const extractors = useQuery({
    queryKey: ["pdf-extractors", "invoice"],
    queryFn: () => listPdfExtractors("invoice"),
  });

  const save = useMutation({
    mutationFn: (input: {
      destination_field: string;
      label: string | null;
      pattern: string | null;
      is_required: boolean;
    }) =>
      editing
        ? updatePdfExtractor(editing.id, input)
        : createPdfExtractor({ import_type: "invoice", ...input }),
    onSuccess: () => {
      toast.success(editing ? "Extractor updated" : "Extractor added");
      setEditing(null);
      setCreating(false);
      void queryClient.invalidateQueries({ queryKey: ["pdf-extractors", "invoice"] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const remove = useMutation({
    mutationFn: async (id: number) => {
      const confirmed = await confirm({
        title: "Delete extractor?",
        description: "This field will no longer be extracted from invoice PDFs.",
        destructive: true,
      });
      if (!confirmed) return;
      await deletePdfExtractor(id);
    },
    onSuccess: () => {
      toast.success("Extractor deleted");
      void queryClient.invalidateQueries({ queryKey: ["pdf-extractors", "invoice"] });
    },
    onError: (error) => toast.error(errorMessage(error)),
  });

  const rows = extractors.data ?? [];

  return (
    <div className="space-y-4">
      <div className="rounded-md border border-muted bg-muted/30 p-4 text-sm text-muted-foreground">
        Invoices arrive as PDFs, so instead of spreadsheet columns each field is pulled out by a{" "}
        <strong>label</strong> (“Invoice Number: INV-123”) or a <strong>regex pattern</strong> whose
        first capture group is the value. Tune these for your suppliers&apos; layouts; invoice PDFs
        are uploaded on the <strong>Invoices</strong> page or arrive by email.
      </div>
      <div className="flex items-center justify-between">
        <Badge variant="secondary">{IMPORT_TYPE_LABELS.invoice}</Badge>
        <div className="flex items-center gap-2">
          <Button size="sm" variant="outline" onClick={() => setBuilderOpen(true)}>
            <MousePointerClick className="h-4 w-4" /> Visual builder
          </Button>
          <Button size="sm" onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" /> Add field
          </Button>
        </div>
      </div>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Field</TableHead>
              <TableHead>Label</TableHead>
              <TableHead>Pattern</TableHead>
              <TableHead>Required</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} className="h-24 text-center text-muted-foreground">
                  No PDF extractors configured
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row) => (
                <TableRow key={row.id}>
                  <TableCell className="font-medium">
                    {row.destination_field}
                    {row.is_required ? <span className="text-destructive"> *</span> : null}
                  </TableCell>
                  <TableCell className="font-mono text-xs">{row.label ?? "—"}</TableCell>
                  <TableCell className="max-w-md truncate font-mono text-xs">
                    {row.pattern ?? "—"}
                  </TableCell>
                  <TableCell>
                    <Switch
                      checked={row.is_required}
                      onCheckedChange={(value) =>
                        void save.mutate({
                          destination_field: row.destination_field,
                          label: row.label ?? "",
                          pattern: row.pattern ?? "",
                          is_required: Boolean(value),
                        })
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing(row)}
                        title="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-destructive"
                        onClick={() => void remove.mutate(row.id)}
                        title="Delete"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>
      {creating ? (
        <ExtractorDialog
          initial={null}
          onClose={() => setCreating(false)}
          onSave={(input) => void save.mutate(input)}
        />
      ) : null}
      {editing ? (
        <ExtractorDialog
          initial={editing}
          onClose={() => setEditing(null)}
          onSave={(input) => void save.mutate(input)}
        />
      ) : null}
      {builderOpen ? (
        <Dialog open onOpenChange={(open) => !open && setBuilderOpen(false)}>
          <DialogContent className="max-w-5xl">
            <DialogHeader>
              <DialogTitle>Visual PDF mapping</DialogTitle>
              <DialogDescription>
                Select a label on the sample invoice to build an extractor rule without writing
                regexes.
              </DialogDescription>
            </DialogHeader>
            <div className="max-h-[72vh] overflow-y-auto">
              <PdfMappingBuilder />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setBuilderOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      ) : null}
    </div>
  );
}

/** Import transparency hub: sources, mappings, run history, manual import. */
export function ImportsPage() {
  const { t } = useTranslation();
  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">{t("nav.imports")}</h1>
        <p className="text-sm text-muted-foreground">
          Manage inbound feeds and their column mappings, inspect every import run, resolve
          quarantined rows and upload files manually.
        </p>
      </div>
      <Tabs defaultValue="sources">
        <TabsList>
          <TabsTrigger value="sources">Sources</TabsTrigger>
          <TabsTrigger value="mappings">Mappings</TabsTrigger>
          <TabsTrigger value="pdf">PDF extractors</TabsTrigger>
          <TabsTrigger value="runs">Runs</TabsTrigger>
          <TabsTrigger value="manual">Manual import</TabsTrigger>
        </TabsList>
        <TabsContent value="sources">
          <SourcesTab />
        </TabsContent>
        <TabsContent value="mappings">
          <MappingsTab />
        </TabsContent>
        <TabsContent value="pdf">
          <PdfExtractorsTab />
        </TabsContent>
        <TabsContent value="runs">
          <RunsTab />
        </TabsContent>
        <TabsContent value="manual">
          <ManualImportTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}
